import { Flex, Text } from '@backstage/ui';

import { KpiCard } from '../KpiCard/KpiCard';
import { KpiRow } from '../KpiRow/KpiRow';
import { PassFailBar } from '../SegmentedBar/SegmentedBar';
import { RiMedalLine } from '@remixicon/react';
import type { TeamProjectDto } from '@internal/scorecards-common';
import { useLevels } from '../../hooks/useLevels';
import { useMemo } from 'react';

function useFailingProjectStats(projects: TeamProjectDto[]) {
  return useMemo(() => {
    let failingTotal = 0;
    for (const p of projects) {
      if (p.failingChecks.length > 0) failingTotal++;
    }
    return { failingTotal };
  }, [projects]);
}

function useFailingChecksByLevel(projects: TeamProjectDto[]) {
  return useMemo(() => {
    const counts = new Map<string, number>();
    const names = new Map<string, string>();
    let others = 0;
    const seenChecks = new Set<string>();

    for (const project of projects) {
      for (const check of project.failingChecks) {
        if (seenChecks.has(check.checkId)) continue;
        seenChecks.add(check.checkId);
        if (check.levelId === null) {
          others++;
        } else {
          counts.set(check.levelId, (counts.get(check.levelId) ?? 0) + 1);
          if (check.levelName) names.set(check.levelId, check.levelName);
        }
      }
    }

    return { counts, names, others };
  }, [projects]);
}

interface TeamKpiRowProps {
  projects: TeamProjectDto[];
}

export const TeamKpiRow = ({ projects }: TeamKpiRowProps) => {
  const { levels } = useLevels();

  const { failingTotal } = useFailingProjectStats(projects);
  const {
    counts: levelCounts,
    names: levelNames,
    others: othersCount,
  } = useFailingChecksByLevel(projects);

  const orderedLevels = useMemo(
    () => [...levels].sort((a, b) => a.id.localeCompare(b.id)),
    [levels],
  );

  return (
    <KpiRow columns={2}>
      <KpiCard label="Failing Projects" colSpan={1}>
        <Flex
          align="center"
          gap="4"
          style={{ marginBottom: 'var(--bui-space-3)' }}
        >
          <Text
            as="span"
            variant="title-large"
            style={{
              color: failingTotal > 0 ? 'var(--zf-sc-fail)' : undefined,
            }}
          >
            {failingTotal}
          </Text>
          <Text as="span" variant="body-small" color="secondary">
            {projects.length - failingTotal} / {projects.length} passing
          </Text>
        </Flex>
        <PassFailBar
          passing={projects.length - failingTotal}
          failing={failingTotal}
          total={projects.length}
        />
      </KpiCard>
      <KpiCard label="Failing Checks by Level" colSpan={1}>
        <Flex direction="column" gap="3">
          {orderedLevels.map(lv => {
            const failing = levelCounts.get(lv.id) ?? 0;
            const name = levelNames.get(lv.id) ?? lv.name;
            return (
              <Flex key={lv.id} align="center" gap="2">
                <Flex align="center" gap="2" grow={1}>
                  <RiMedalLine
                    size={20}
                    style={{
                      color: `var(--zf-sc-level-${lv.id})`,
                      flexShrink: 0,
                    }}
                  />
                  <Text as="span" variant="body-medium" color="primary">
                    {name}
                  </Text>
                </Flex>
                <Text as="span" variant="body-medium" color="primary">
                  {failing}
                </Text>
              </Flex>
            );
          })}
          {othersCount > 0 && (
            <Flex align="center" gap="2">
              <Flex align="center" gap="2" grow={1}>
                <RiMedalLine
                  size={20}
                  style={{
                    color: 'var(--zf-sc-level-others)',
                    flexShrink: 0,
                  }}
                />
                <Text as="span" variant="body-medium" color="primary">
                  Others
                </Text>
              </Flex>
              <Text as="span" variant="body-medium" color="primary">
                {othersCount}
              </Text>
            </Flex>
          )}
        </Flex>
      </KpiCard>
    </KpiRow>
  );
};
